/**
 * DrunkModeController — řídí drunk-mode efekt: spočítá stav (resolveDrunkState),
 * nastaví třídu `bw-drunk` na <body> a `scale` SVG filtru. Sám nic nevykresluje.
 *
 * Mountuje se jednou v `App`. Vizuální efekt se projeví jen na elementech s
 * `[data-drunk]` (user-facing kořeny) — admin marker nemá, takže nikdy nevlní.
 */
import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api-client.js";
import { useAuth } from "../lib/use-auth.js";
import { useDrunkConfig } from "../lib/use-drunk-config.js";
import {
  loadDrunkConfig,
  resolveDrunkState,
  intensityToScale,
  DRUNK_OPTIN_KEY,
  DRUNK_CHANGED_EVENT,
  type DrunkTeamColor,
  type TeamBeerTotals,
} from "../lib/drunk-mode.js";

/** Náhodný plánovač jednoho efektu — střídá „rest" (síla 0) a „on" (0→1→0). */
interface Scheduler {
  write: (env: number) => void; // zápis síly do CSS proměnné / SVG atributu
  mode: "rest" | "on";
  start: number; // performance.now() začátku aktuální fáze
  dur: number; // délka aktuální fáze (ms)
  env: number; // aktuální síla 0..1
  last: number; // poslední zapsaná síla (change-detection)
}

function hasToken(): boolean {
  try {
    return Boolean(localStorage.getItem("bw:token") ?? localStorage.getItem("bw_token"));
  } catch {
    return false;
  }
}

/** Beze side-effectů na /login: beers tahej jen u přihlášeného (token). */
async function fetchMyBeers(): Promise<number | null> {
  if (!hasToken()) return null;
  try {
    const me = await api.get<{ id: string }>("/players/me");
    const hist = await api.get<{ items: Array<{ isActive: boolean; consumedBeers: number }> }>(
      `/players/${me.id}/history`,
    );
    const active = hist.items.find((i) => i.isActive);
    return active?.consumedBeers ?? 0;
  } catch {
    return null;
  }
}

/**
 * Pro team režimy: součty piv per tým (veřejný scoreboard) + týmová barva
 * přihlášeného hráče (z jeho řádku ve snapshotu). Nepřihlášený → teamColor null.
 */
async function fetchTeamStanding(): Promise<
  { teamColor: DrunkTeamColor | null; teamBeers: TeamBeerTotals } | null
> {
  try {
    const res = await fetch("/api/scoreboard", { headers: { Accept: "application/json" } });
    if (!res.ok) return null;
    const snap = (await res.json()) as {
      rows?: Array<{ playerId: string; teamColor: DrunkTeamColor | "vip" | null }>;
      teamTotals?: Record<"blue" | "green" | "gold", { totalBeers?: number } | undefined>;
    };
    const teamBeers: TeamBeerTotals = {
      blue: snap.teamTotals?.blue?.totalBeers ?? 0,
      green: snap.teamTotals?.green?.totalBeers ?? 0,
      gold: snap.teamTotals?.gold?.totalBeers ?? 0,
    };
    let teamColor: DrunkTeamColor | null = null;
    if (hasToken()) {
      const me = await api.get<{ id: string }>("/players/me");
      const row = snap.rows?.find((r) => r.playerId === me.id);
      const c = row?.teamColor;
      teamColor = c === "blue" || c === "green" || c === "gold" ? c : null;
    }
    return { teamColor, teamBeers };
  } catch {
    return null;
  }
}

/** True pro režimy závislé na týmovém standingu. */
function isTeamMode(mode: string): boolean {
  return mode === "byTeamLosing" || mode === "byTeamWinning";
}

function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(
    () => typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches,
  );
  useEffect(() => {
    const mq = window.matchMedia?.("(prefers-reduced-motion: reduce)");
    if (!mq) return undefined;
    const h = () => setReduced(mq.matches);
    mq.addEventListener("change", h);
    return () => mq.removeEventListener("change", h);
  }, []);
  return Boolean(reduced);
}

export function DrunkModeController() {
  const config = useDrunkConfig();
  const reducedMotion = usePrefersReducedMotion();
  const { role } = useAuth();
  const [optInEnabled, setOptInEnabled] = useState(
    () => (typeof localStorage !== "undefined" ? localStorage.getItem(DRUNK_OPTIN_KEY) === "1" : false),
  );
  const [consumedBeers, setConsumedBeers] = useState<number | null>(null);
  const [teamStanding, setTeamStanding] = useState<
    { teamColor: DrunkTeamColor | null; teamBeers: TeamBeerTotals } | null
  >(null);
  const [nodeCtx, setNodeCtx] = useState<{ nodeRole: "local" | "public" | null; localTerminal: boolean }>(
    { nodeRole: null, localTerminal: false },
  );
  const loadedRef = useRef(false);

  // Načti config jednou při bootu.
  useEffect(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;
    void loadDrunkConfig();
  }, []);

  // Node kontext z /healthz (node role + zda je klient na local terminálu).
  // Potřebné pro „herní" režimy (gate obsluhy baru), fetchni proto jen tam.
  useEffect(() => {
    if (config.mode !== "byBeers" && !isTeamMode(config.mode)) return;
    let cancelled = false;
    void fetch("/healthz", { headers: { Accept: "application/json" } })
      .then((r) => (r.ok ? r.json() : null))
      .then((j: { node_role?: string; local_terminal?: boolean } | null) => {
        if (cancelled || !j) return;
        setNodeCtx({
          nodeRole: j.node_role === "public" ? "public" : "local",
          localTerminal: j.local_terminal === true,
        });
      })
      .catch(() => {
        /* ponech default — efekt se zobrazí normálně */
      });
    return () => {
      cancelled = true;
    };
  }, [config.mode]);

  // Per-user opt-in přepínač (DrunkToggle vyšle event).
  useEffect(() => {
    const h = () => setOptInEnabled(localStorage.getItem(DRUNK_OPTIN_KEY) === "1");
    window.addEventListener(DRUNK_CHANGED_EVENT, h);
    return () => window.removeEventListener(DRUNK_CHANGED_EVENT, h);
  }, []);

  // byBeers: počet piv přihlášeného hráče (+ refresh na bw:refetch).
  useEffect(() => {
    if (config.mode !== "byBeers") {
      setConsumedBeers(null);
      return undefined;
    }
    let cancelled = false;
    const refresh = () => {
      void fetchMyBeers().then((b) => {
        if (!cancelled) setConsumedBeers(b);
      });
    };
    refresh();
    window.addEventListener("bw:refetch", refresh);
    return () => {
      cancelled = true;
      window.removeEventListener("bw:refetch", refresh);
    };
  }, [config.mode]);

  // team režimy: standing týmů + barva hráče (+ refresh na bw:refetch).
  useEffect(() => {
    if (!isTeamMode(config.mode)) {
      setTeamStanding(null);
      return undefined;
    }
    let cancelled = false;
    const refresh = () => {
      void fetchTeamStanding().then((t) => {
        if (!cancelled) setTeamStanding(t);
      });
    };
    refresh();
    window.addEventListener("bw:refetch", refresh);
    return () => {
      cancelled = true;
      window.removeEventListener("bw:refetch", refresh);
    };
  }, [config.mode]);

  const state = resolveDrunkState({
    config,
    optInEnabled,
    consumedBeers,
    teamColor: teamStanding?.teamColor ?? null,
    teamBeers: teamStanding?.teamBeers ?? null,
    reducedMotion,
    role,
    nodeRole: nodeCtx.nodeRole,
    localTerminal: nodeCtx.localTerminal,
  });

  // Refs pro náhodné plánovače (jeden rAF).
  const rafRef = useRef<number | null>(null);
  const schedRef = useRef<Scheduler[] | null>(null);

  // Každý ze tří efektů (vlna / blur / houpání) má vlastní NEZÁVISLÝ náhodný
  // plánovač: střídá KLID (síla 0, náhodně 5–15 s) a EFEKT (síla 0→1→0, náhodně
  // 3–6 s; náběh/útlum úměrný délce). Tím se efekty objevují nahodile a volně se
  // překrývají. Jeden rAF počítá sílu (0..1) každého a zapisuje JEN při změně →
  // displacement se přepočítává jen během ramp, v klidu i na špičce stojí.
  useEffect(() => {
    const REST_MAX = 15000; // max délka klidu
    const REST_MIN = 5000; // min délka klidu
    const ON_MAX = 6000; // max délka efektu
    const ON_MIN = 3000; // min délka efektu
    const ease = (x: number) => (x < 0.5 ? 2 * x * x : 1 - Math.pow(-2 * x + 2, 2) / 2);
    const rand = (min: number, max: number) => min + Math.random() * (max - min);
    const disp = document.getElementById("bw-drunk-displace");
    const maxScale = intensityToScale(state.intensity);

    // Síla efektu uvnitř „on" fáze (náběh/hold/útlum, ramp = 40 % délky).
    const onEnv = (t: number, dur: number) => {
      const ramp = dur * 0.4;
      if (t < ramp) return ease(t / ramp);
      if (t > dur - ramp) return ease(Math.max(0, (dur - t) / ramp));
      return 1;
    };

    const writeSway = (env: number) => document.body.style.setProperty("--bw-drunk-sway", String(state.intensity * env));
    const writeBlur = (env: number) => document.body.style.setProperty("--bw-drunk-blur", String(state.intensity * env));
    const writeWave = (env: number) => disp?.setAttribute("scale", String(maxScale * env));

    // Inicializuj plánovače (jednou). Náhodný počáteční klid → efekty nestartují naráz.
    if (schedRef.current == null) {
      const make = (write: (e: number) => void): Scheduler => ({
        write,
        mode: "rest",
        start: performance.now(),
        dur: rand(0, REST_MAX),
        env: 0,
        last: -1,
      });
      schedRef.current = [
        make(writeWave), // vlna
        make(writeBlur), // rozostření
        make(writeSway), // houpání
      ];
    } else {
      // Při změně intenzity aktualizuj write fce (drží svůj náhodný stav) a vynuť
      // přepis i během konstantní fáze (jinak by change-detection změnu zamlčel).
      const fns: Array<(e: number) => void> = [writeWave, writeBlur, writeSway];
      schedRef.current.forEach((s, i) => {
        const fn = fns[i];
        if (fn) s.write = fn;
        s.last = -1;
      });
    }

    const apply = (s: Scheduler) => {
      if (s.env !== s.last) {
        s.write(s.env);
        s.last = s.env;
      }
    };

    if (rafRef.current != null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }

    if (state.active) {
      document.body.classList.add("bw-drunk");
      const loop = (now: number) => {
        for (const s of schedRef.current!) {
          let elapsed = now - s.start;
          if (elapsed >= s.dur) {
            s.mode = s.mode === "rest" ? "on" : "rest";
            s.start = now;
            s.dur = s.mode === "on" ? rand(ON_MIN, ON_MAX) : rand(REST_MIN, REST_MAX);
            elapsed = 0;
          }
          s.env = s.mode === "on" ? onEnv(elapsed, s.dur) : 0;
          apply(s);
        }
        rafRef.current = requestAnimationFrame(loop);
      };
      rafRef.current = requestAnimationFrame(loop);
    } else {
      // Skutečné vypnutí: plynule sjeď VŠECHNY brány z aktuální síly na 0, pak ukliď.
      const scheds = schedRef.current!;
      const from = scheds.map((s) => s.env);
      const start = performance.now();
      const DUR = 1200;
      const down = (now: number) => {
        const t = Math.min(1, (now - start) / DUR);
        const k = 1 - ease(t);
        scheds.forEach((s, i) => {
          s.env = (from[i] ?? 0) * k;
          apply(s);
        });
        if (t < 1) {
          rafRef.current = requestAnimationFrame(down);
        } else {
          rafRef.current = null;
          document.body.classList.remove("bw-drunk");
          document.body.style.removeProperty("--bw-drunk-sway");
          document.body.style.removeProperty("--bw-drunk-blur");
          // reset stavu, ať příští zapnutí začne čistě
          scheds.forEach((s) => {
            s.mode = "rest";
            s.start = performance.now();
            s.dur = rand(0, REST_MAX);
            s.env = 0;
            s.last = -1;
          });
        }
      };
      rafRef.current = requestAnimationFrame(down);
    }

    return () => {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    };
  }, [state.active, state.intensity]);

  // Cleanup při unmountu (defenzivně).
  useEffect(() => {
    return () => {
      document.body.classList.remove("bw-drunk");
      document.getElementById("bw-drunk-displace")?.setAttribute("scale", "0");
      document.body.style.removeProperty("--bw-drunk-sway");
      document.body.style.removeProperty("--bw-drunk-blur");
    };
  }, []);

  return null;
}
